import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Download, Printer } from 'lucide-react';
import { bookContent } from '../data/bookContent';
import { ScriptureQuote } from '../components/ScriptureQuote';
import { PrayerCard } from '../components/PrayerCard';
import { JournalPrompt } from '../components/JournalPrompt';
import { AffirmationCard } from '../components/AffirmationCard';
import { QuoteBlock } from '../components/QuoteBlock';

export const FreeChapter1 = () => {
  const chapter = bookContent.chapters[0]; // Chapter 1

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div data-testid="free-chapter-1" className="min-h-screen bg-cream pb-16">
      {/* Header */}
      <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-stone-100 z-50">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link 
            to="/free"
            data-testid="back-to-freebies"
            className="flex items-center gap-2 text-charcoal-light hover:text-sage transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back</span>
          </Link>
          <span className="text-sm font-medium text-gold">FREE PREVIEW</span>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 text-charcoal-light hover:text-sage transition-colors"
          >
            <Printer className="w-5 h-5" />
            <span className="hidden sm:inline">Print</span>
          </button>
        </div>
      </div>

      {/* Hero Image */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <img 
          src={chapter.image} 
          alt={chapter.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-cream via-cream/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-3xl mx-auto">
            <span className="inline-block px-3 py-1 bg-gold/20 text-gold text-sm font-medium rounded-full mb-3">
              Chapter 1 — Free Preview
            </span>
            <h1 className="font-heading text-4xl md:text-5xl text-charcoal animate-fade-in">
              {chapter.title}
            </h1>
            <p className="font-heading text-xl text-charcoal-light italic mt-2">
              {chapter.subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Opening Scripture */}
        <ScriptureQuote 
          text={chapter.scripture.text} 
          reference={chapter.scripture.reference} 
        />

        {/* Main Content */}
        <div className="prose prose-lg max-w-none my-10">
          {chapter.content.split('\n\n').map((paragraph, index) => (
            <p 
              key={index} 
              className="text-charcoal/90 leading-relaxed mb-6 animate-fade-in"
            >
              {paragraph}
            </p>
          ))}
        </div>

        {/* Quote */}
        <QuoteBlock quote={chapter.quote} />

        {/* Affirmation */}
        <AffirmationCard affirmation={chapter.affirmation} />

        {/* Journal Section */}
        <div className="my-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-sage/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-sage" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <h2 className="font-heading text-2xl text-charcoal">Reflection Questions</h2>
          </div>
          <p className="text-charcoal-light mb-6">
            Take time to honestly reflect on these questions. Your responses are saved privately on your device.
          </p>
          {chapter.journalQuestions.map((question, index) => (
            <JournalPrompt 
              key={index}
              question={question}
              chapterId={chapter.id}
              questionIndex={index}
            />
          ))}
        </div>

        {/* Closing Prayer */}
        <PrayerCard prayer={chapter.prayer} />

        {/* CTA to Full Book */}
        <div className="mt-16 p-8 bg-gradient-to-r from-sage-light/30 to-sky-light/30 rounded-2xl text-center">
          <h3 className="font-heading text-2xl text-charcoal mb-3">
            Continue Your Breakthrough Journey
          </h3>
          <p className="text-charcoal-light mb-6 max-w-lg mx-auto">
            This is just the beginning. The full Breakthrough guide includes 5 more transformative chapters 
            covering shame, guilt, victim mentality, forgiveness, and discovering your true potential.
          </p>
          <Link
            to="/contents"
            data-testid="get-full-book-cta"
            className="inline-flex items-center gap-2 px-8 py-4 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all shadow-lg"
          >
            Get the Full Guide
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>

        {/* Also Get 5 Lies */}
        <div className="mt-8 text-center">
          <Link
            to="/free/5-lies"
            className="text-sage hover:text-sage-dark font-medium"
          >
            Also check out: 5 Lies Survivors Believe →
          </Link>
        </div>

        {/* Copyright Footer */}
        <div className="mt-12 pt-6 border-t border-stone-200 text-center">
          <p className="text-sm text-charcoal-light">By Janelle McGowan-Charles</p>
          <p className="text-xs text-charcoal-light/70 mt-1">Purpose Reclaimed Coaching</p>
          <p className="text-xs text-charcoal-light/60 mt-2">© {new Date().getFullYear()} Janelle McGowan-Charles, Purpose Reclaimed Coaching. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};
